import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import OrganizationTaskService from '../services/OrganizationTaskService';
import { RootState } from './store';
import { IOrganizationTask, IOrganizationTaskRequest } from "@ontime/types";

// --- LocalStorage Helpers ---
const loadState = (): OrganizationTaskState => {
    try {
        const serializedState = localStorage.getItem('organizationTasks');
        return serializedState ? JSON.parse(serializedState) : { organizationTasks: [], loading: false, error: null };
    } catch (error) {
        console.error('Could not load service providers state:', error);
        return { organizationTasks: [], loading: false, error: null };
    }
};

const saveState = (state: OrganizationTaskState) => {
    try {
        localStorage.setItem('organizationTasks', JSON.stringify(state));
    } catch (error) {
        console.error('Could not save service providers state:', error);
    }
};

// --- Async Thunks ---
export const fetchOrganizationTasks = createAsyncThunk(
    'organizationTasks/fetchAll',
    async ({ organizationId, showDeleted }: { organizationId: string, showDeleted: boolean }) => {
        try {
            return await OrganizationTaskService.GetList(organizationId, showDeleted);
        } catch (error) {
            return null;
        }
    }
);

export const getOrganizationTaskAsync = createAsyncThunk(
    'organizationTasks/get',
    async ({ organizationId, organizationTaskId }: { organizationId: string; organizationTaskId: string }) => {
        try {
            return await OrganizationTaskService.Get(organizationId, organizationTaskId);
        } catch (error) {
            return null;
        }
    }
);

export const createOrganizationTaskAsync = createAsyncThunk(
    'organizationTasks/create',
    async ({ organizationId, data }: { organizationId: string; data: IOrganizationTaskRequest }) => {
        try {
            return await OrganizationTaskService.Create(organizationId, data);
        } catch (error) {
            return null;
        }
    }
);

export const updateOrganizationTaskAsync = createAsyncThunk(
    'organizationTasks/update',
    async ({ organizationId, data }: { organizationId: string; data: IOrganizationTaskRequest }) => {
        try {
            return await OrganizationTaskService.Update(organizationId, data);
        } catch (error) {
            return null;
        }
    }
);

export const deleteOrganizationTaskAsync = createAsyncThunk(
    'organizationTasks/delete',
    async ({ organizationId, organizationTaskId,currentIsDeleted }: { organizationId: string; organizationTaskId: string , currentIsDeleted: boolean}) => {
        try {
            return await OrganizationTaskService.Delete(organizationId, organizationTaskId,currentIsDeleted);
        } catch (error) {
            return null;
        }
    }
);

// --- State Interface ---
interface OrganizationTaskState {
    organizationTasks: IOrganizationTask[];
    loading: boolean;
    error: string | null;
}

const initialState: OrganizationTaskState = loadState();

// --- Slice ---
const organizationTaskSlice = createSlice({
    name: 'organizationTasks',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchOrganizationTasks.pending, (state) => {
                state.organizationTasks = [];
                state.loading = true;
                state.error = null;
                saveState(state);
            })
            .addCase(fetchOrganizationTasks.fulfilled, (state, action) => {
                state.loading = false;
                state.organizationTasks = action.payload ?? [];
                saveState(state);
            })
            .addCase(fetchOrganizationTasks.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                saveState(state);
            })
            .addCase(createOrganizationTaskAsync.fulfilled, (state, action) => {
                if (action.payload) {
                    state.organizationTasks.unshift(action.payload);
                    saveState(state);
                }
            })
            .addCase(updateOrganizationTaskAsync.fulfilled, (state, action) => {
                if (action.payload) {
                    const index = state.organizationTasks.findIndex(sp => sp.organizationTaskId === action.payload?.organizationTaskId);
                    if (index !== -1) {
                        state.organizationTasks[index] = action.payload;
                        saveState(state);
                    }
                }
            })
            .addCase(deleteOrganizationTaskAsync.fulfilled, (state, action) => {
                state.organizationTasks = state.organizationTasks.filter(sp => sp.organizationTaskId !== action.payload?.organizationTaskId);
                saveState(state);
            });
    }
});

export default organizationTaskSlice.reducer;
export const selectOrganizationTasks = (state: RootState) => state.organizationTasks.organizationTasks;
export const selectOrganizationTasksLoading = (state: RootState) => state.organizationTasks.loading;
export const selectOrganizationTasksError = (state: RootState) => state.organizationTasks.error;